import { FormMapping } from '@form/FormMapping';
import { Form } from 'components/Form/base/Form';
import { FormDivider, FormInput, FormRow } from 'components/Form/next';
import { SpaceGroup } from 'components/SpaceBooking/types';
import Joi from 'joi';
import { omit } from 'lodash';
import { useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { FellowRulesFieldArray, fellowRulesValidationRules } from './FellowRulesFieldArray';
import { GuestRulesFieldArray, guestRulesValidationRules } from './GuestRulesFieldArray';

interface Props {
    clubSlug: string;
    group?: SpaceGroup;
    callback: (spaceGroup?: SpaceGroup) => void;
}

export const SpaceGroupForm = ({ clubSlug, group, callback = () => {} }: Props) => {
    const api = useApi();
    const [isLoading, setIsLoading] = useState(false);

    const onSubmit = (data) => {
        setIsLoading(true);
        return api({
            url: `space/group/${group?.id || clubSlug}`,
            method: group ? 'PUT' : 'POST',
            data: {
                ...omit(data, 'form'),
                form_mapping: { ...(data?.form_mapping || {}) },
                spaces: data.spaces?.map((space) => space.id),
                form_id: data.form
            },
            onBefore: () => setIsLoading(false),
            onSuccess: ({ data }) => callback(data)
        });
    };
    const schema = Joi.object({
        name: Joi.string().required().label('Name'),
        description: Joi.string().allow('', null).label('Beschreibung'),
        max_spaces: Joi.number().allow('', null).min(0).label('Maximale Plätze'),
        form: Joi.number().min(1).allow('', null).label('Formular').messages({
            'number.base': '***Bitte wähle ein Formular aus.'
        }),
        rules_per_guest_count: guestRulesValidationRules,
        rules_per_fellow_count: fellowRulesValidationRules,
        rules_all_fellow_count: guestRulesValidationRules
    }).unknown();

    return (
        <Form
            onSubmit={onSubmit}
            validationSchema={schema}
            setDefaultValuesOnRerender={false}
            defaultValues={{
                ...group,
                form: group?.form_id
            }}
        >
            <FormDivider title="Allgemein" />
            <FormInput name="name" label="Name" />
            <FormInput.WYSIWYG name="description" label="Beschreibung" />
            <FormDivider title="Online Zahlungen" />
            <FormInput.Checkbox name="allow_online_payments" label="Online Zahlungen erlauben" />
            <FormDivider title="Richtlinien für Mitspieler und Gäste" />
            <FormInput
                type="number"
                name="max_spaces"
                label="Maximale Anzahl an Plätzen welche auf einmal gebucht werden können"
            />
            <FormDivider title="Mitspielerregeln nach Anzahl" />

            <FormInput.Checkbox name="allow_fellows" label="Angabe von Mitspielern erlauben" />

            <FormInput.Checkbox name="deny_guests" label="Gastmitspieler verbieten" />

            <FormInput.Checkbox name="allow_edit_fellows" label="Bearbeitung von Mitspielern erlauben" />

            <FormRow>
                <FormInput type="number" name="min_fellows" placeholder="egal" label="Minimale Anzahl an Mitspielern" />
                <FormInput type="number" name="max_fellows" placeholder="egal" label="Maximale Anzahl an Mitspielern" />
            </FormRow>
            <GuestRulesFieldArray label="Mitspielerregel nach Anzahl hinzufügen" name="rules_all_fellow_count" />
            <FormDivider title="Regeln für Mitspieler nach Anzahl, welche Mitglied im Verein sind" />
            <FellowRulesFieldArray
                label="Vereins Mitspielerregel nach Anzahl hinzufügen"
                name="rules_per_fellow_count"
            />
            <FormDivider title="Regeln für Mitspieler nach Anzahl, welche KEIN Mitglied im Verein sind" />
            <FormInput.Checkbox
                name="fellow_email_optional"
                label="Mitspieler E-Mail optional (Achtung! Folgende Regeln können ohne E-Mail Adresse nicht angewendet werden)"
            />
            <FormInput
                type="number"
                name="max_guests"
                placeholder="egal"
                label="Maximale Anzahl an Gästen (Mitspieler, welche kein Mitglied im Verein sind)"
            />
            <GuestRulesFieldArray name="rules_per_guest_count" />
            <FormDivider title="Formulare" />
            <FormInput.Formular name="form" />
            <FormMapping />
            <FormInput.Submit className="mt-3" isLoading={isLoading}>
                Speichern
            </FormInput.Submit>
        </Form>
    );
};
